<template>
  <div>
    <div class="detail-card">
      <div class="flex items-start">
        <h3 class="detail-card__title">領取週期</h3>
        <a href="https://help.takeorder.tw/2026/03/19/regular_group_buy" target="_blank" rel="noopener noreferrer">
          <span class="text-primary-500">（設定教學）</span>
        </a>
      </div>
      <div class="align-text-bottom flex flex-wrap gap-6 mt-8">
        <b-radio
          v-model="receivePeriod.type"
          :key="SCHEDULE_TYPES.PERIOD"
          :disabled="viewMode"
          native-value="period"
          @input="onTypeChange($event); onEdit()"
        >
          <span>每天</span>
        </b-radio>
        <b-radio
          v-model="receivePeriod.type"
          :key="SCHEDULE_TYPES.WEEKLY"
          :disabled="viewMode"
          native-value="weekly"
          @input="onTypeChange($event); onEdit()"
        >
          <span>每週</span>
        </b-radio>
        <b-radio
          v-model="receivePeriod.type"
          :key="SCHEDULE_TYPES.DOUBLE_WEEKLY"
          :disabled="viewMode"
          native-value="double_weekly"
          @input="onTypeChange($event); onEdit()"
        >
          <span>每兩週</span>
        </b-radio>
        <b-radio
          v-model="receivePeriod.type"
          :key="SCHEDULE_TYPES.MONTHLY"
          :disabled="viewMode"
          native-value="monthly"
          @input="onTypeChange($event); onEdit()"
        >
          <span>每月</span>
        </b-radio>
      </div>
      <div
        v-if="
          receivePeriod.type === SCHEDULE_TYPES.WEEKLY ||
          receivePeriod.type === SCHEDULE_TYPES.DOUBLE_WEEKLY
        "
        class="flex flex-wrap gap-4 mt-8"
      >
        <b-checkbox
          v-for="weekday in weekdayOptions"
          v-model="receivePeriod.weekdays"
          :key="weekday.label"
          :disabled="viewMode"
          :native-value="weekday.value"
          @input="onEdit"
        >
          <span>{{ weekday.label }}</span>
        </b-checkbox>
      </div>
      <b-field
        v-if="v$.receivePeriod.weekdays.$error"
        :message="v$.receivePeriod.weekdays.$errors[0].$message"
        type="is-danger"
      />
      <div
        v-if="receivePeriod.type === SCHEDULE_TYPES.MONTHLY"
        class="flex flex-wrap gap-x-6 gap-y-2 mt-8"
      >
        <b-checkbox
          v-for="monthDay in monthDayOptions"
          v-model="receivePeriod.monthDays"
          :key="monthDay.label"
          :disabled="viewMode"
          :native-value="monthDay.value"
          @input="onTypeChange(); onEdit()"
        >
          <div class="min-w-[36px]">{{ monthDay.label }}</div>
        </b-checkbox>
      </div>
      <b-field
        v-if="v$.receivePeriod.monthDays.$error"
        :message="v$.receivePeriod.monthDays.$errors[0].$message"
        type="is-danger"
      />
      <div class="border-b border-light-gray-500 my-7" />
      <div
        class="
          flex
          flex-wrap
          items-baseline
          justify-start
          mb-4
      "
      >
        <h3 class="font-medium mr-4">
          週期範圍
        </h3>
        <span class="text-gray-500 text-sm">
          系統將以領取週期為判斷基準，自動建立領取日於週期範圍內的訂購。
        </span>
      </div>
      <div class="flex flex-wrap gap-6 items-center">
        <span class="text-gray-500">起始日</span>
        <b-datepicker
          v-model="receivePeriod.startDate"
          :date-formatter="formatDate"
          :disabled="viewMode"
          :mobile-native="false"
          :unselectable-dates="disableReceivePeriodStartDate"
          append-to-body
          class="w-64"
          icon="calendar-week"
          locale="zh-TW"
          position="is-top-right"
          @input="onEdit"
        />
        <span class="text-gray-500">截止日</span>
        <b-datepicker
          v-model="receivePeriod.endDate"
          :date-formatter="formatDate"
          :disabled="viewMode"
          :mobile-native="false"
          :unselectable-dates="disableReceivePeriodEndDate"
          append-to-body
          class="w-64"
          icon="calendar-week"
          locale="zh-TW"
          position="is-top-right"
          @input="onEdit"
        />
      </div>
      <div class="border-b border-light-gray-500 mb-8 mt-6" />
      <h3 class="font-medium mb-4">
        領取時段設定
      </h3>
      <div
        v-for="(section, index) in receivePeriod.sections"
        :key="`receive-section-${index}`"
        class="mb-4"
      >
        <div class="flex gap-1 items-center">
          <div class="flex flex-wrap items-center gap-6">
            <span class=" text-gray-500">
              {{ index + 1 }}.
            </span>
            <span class=" text-gray-500">
              當天
            </span>
            <b-timepicker
              v-model="section.startTime"
              :disabled="viewMode"
              :mobile-native="false"
              :time-formatter="formatTime"
              append-to-body
              class="w-32"
              hour-format="12"
              hours-label="小時"
              locale="zh-TW"
              minutes-label="分鐘"
              @input="onEdit"
            />
            <span class=" text-gray-500">至</span>
            <b-select
              v-model="section.endDay"
              :disabled="viewMode"
              class="w-32"
              expanded
              @input="onEdit">
              <option
                v-for="option in receiveSectionEndDayOptions"
                :value="option.value"
                :key="option.value">
                {{ option.label }}
              </option>
            </b-select>
            <b-timepicker
              v-model="section.endTime"
              :disabled="viewMode"
              :mobile-native="false"
              :time-formatter="formatTime"
              append-to-body
              class="w-32"
              hour-format="12"
              hours-label="小時"
              locale="zh-TW"
              minutes-label="分鐘"
              @input="onEdit"
            />
          </div>
          <a
            v-if="receivePeriod.sections.length > 1 && !viewMode"
            href="#"
            class="period-action flex-grow-0 flex-shrink-0"
            @click.prevent="onDeleteReceiveSection(index); onEdit()"
          >
            <span class="icon-trash" />
          </a>
        </div>
        <b-field
          v-if="v$.receivePeriod.sections.$error"
          :message="v$.receivePeriod.sections.$errors[0].$message[index]"
          type="is-danger"
        />
      </div>
      <div v-if="!viewMode" class="mt-4">
        <w-button color-type="secondary" type="link" @click="onAddReceiveSection($event); onEdit()">
          <i class="icon-plus mr-2 text-sm" />
          <span class="text-sm">添加更多時段</span>
        </w-button>
      </div>
    </div>
    <div class="detail-card">
      <div
        class="
          flex
          flex-wrap
          items-baseline
          justify-start
          mb-6
        "
      >
        <h3 class="font-medium mr-4">開放訂購時間範圍</h3>
      </div>
      <div class="flex flex-col gap-4">
        <span class="text-gray-500">起始時間</span>
        <div class="flex flex-wrap gap-4 items-center">
          <b-select
            v-model="orderPeriod.startDay"
            :disabled="viewMode"
            class="w-48"
            expanded
            @input="onStartDayChange($event); onEdit()">
            <option
              v-for="option in orderStartDayOptions"
              :disabled="option.disabled"
              :value="option.value"
              :key="option.value">
              {{ option.label }}
            </option>
          </b-select>
          <b-datepicker
            v-if="orderPeriod.startDay === -1"
            v-model="orderPeriod.startAtSameTime"
            :date-formatter="formatDate"
            :disabled="viewMode"
            :mobile-native="false"
            :unselectable-dates="disableStartAtSameTimeDate"
            append-to-body
            class="w-48"
            icon="calendar-week"
            locale="zh-TW"
            position="is-top-right"
            @input="onEdit"
          />
          <b-timepicker
            v-model="orderPeriod.startTime"
            :disabled="viewMode"
            :mobile-native="false"
            :time-formatter="formatTime"
            append-to-body
            class="w-32"
            hour-format="12"
            hours-label="小時"
            locale="zh-TW"
            minutes-label="分鐘"
            @input="onEdit"
            />
        </div>
        <span class="text-gray-500">結束時間</span>
        <div class="flex flex-wrap gap-4 items-center">
          <b-select
            v-model="orderPeriod.endDay"
            :disabled="viewMode"
            class="w-48"
            expanded
            @input="onEndDayChange($event); onEdit()">
            <option
              v-for="option in orderEndDayOptions"
              :disabled="option.disabled"
              :value="option.value"
              :key="option.value">
              {{ option.label }}
            </option>
          </b-select>
          <b-datepicker
            v-if="orderPeriod.endDay === -1"
            v-model="orderPeriod.endAtSameTime"
            :date-formatter="formatDate"
            :disabled="viewMode"
            :mobile-native="false"
            :unselectable-dates="disableEndAtSameTimeDate"
            append-to-body
            class="w-48"
            icon="calendar-week"
            locale="zh-TW"
            position="is-top-right"
            @input="onEdit"
          />
          <b-timepicker
            v-model="orderPeriod.endTime"
            :disabled="viewMode"
            :mobile-native="false"
            :time-formatter="formatTime"
            append-to-body
            class="w-32"
            hour-format="12"
            hours-label="小時"
            locale="zh-TW"
            minutes-label="分鐘"
            @input="onEdit"
            />
        </div>
        <b-field
          v-if="orderPeriodError"
          :message="orderPeriodErrorMessage"
          type="is-danger"
        />
      </div>
    </div>
    <div class="detail-card">
      <h3 class="detail-card__title">公告時間</h3>
      <div class="flex flex-wrap gap-4 items-center">
        <span class="text-gray-500 mr-2">開放訂購日</span>
        <b-select
          v-model="orderCreate.day"
          :disabled="viewMode"
          class="w-32"
          expanded
          @input="onEdit">
          <option
            v-for="option in orderCreateDayOptions"
            :value="option.value"
            :key="option.value">
            {{ option.label }}
          </option>
        </b-select>
        <b-timepicker
          v-model="orderCreate.time"
          :disabled="viewMode"
          :mobile-native="false"
          :time-formatter="formatTime"
          append-to-body
          class="w-32"
          hour-format="12"
          hours-label="小時"
          locale="zh-TW"
          minutes-label="分鐘"
          @input="onEdit"
        />
        <span class="text-gray-500">公告</span>
      </div>
      <b-field
        v-if="v$.orderCreate.$error"
        :message="v$.orderCreate.$errors[0].$message"
        type="is-danger"
      />
    </div>
  </div>
</template>

<script>
import SchedulePeriodSetting from './SchedulePeriodSetting';

export default SchedulePeriodSetting;
</script>

<style lang="scss" scoped>
@import 'SchedulePeriodSetting';
</style>
